import { useState, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import confetti from "canvas-confetti";
import { useToast } from "@/hooks/use-toast";
import { useBookingOrchestration, type BookingResult, type DuplicateCandidate, type DoubleBookingConflict } from "@/hooks/use-booking-orchestration";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { EventbriteEventPicker } from "@/components/eventbrite-event-picker";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Check, ChevronsUpDown, Loader2, Plus, AlertCircle, MapPin, DollarSign, Calendar, Clock, Globe } from "lucide-react";
import { cn } from "@/lib/utils";
import { AddressAutocomplete } from "./spreadsheet/address-autocomplete";
import type { ClientTypeRecord, EventOffering, Talent } from "@shared/schema";

const triggerConfetti = () => {
  const duration = 1667;
  const animationEnd = Date.now() + duration;
  const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };
  const randomInRange = (min: number, max: number) => Math.random() * (max - min) + min;
  const interval = setInterval(() => {
    const timeLeft = animationEnd - Date.now();
    if (timeLeft <= 0) return clearInterval(interval);
    const particleCount = 50 * (timeLeft / duration);
    confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } });
    confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } });
  }, 250);
};

interface Client {
  id: string;
  accountName: string | null;
  address: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
  rate: string | number | null;
  bookingDetails: string | null;
  emailAddress: string | null;
  phoneNumber: string | null;
  contactName: string | null;
  regionId: string | null;
  clientType: string | null;
  rep: string | null;
  timezone?: string | null;
  talentId?: string | null;
  eventOfferingId?: string | null;
}

interface ProfileData {
  role?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

interface LogEventDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preselectedClient?: Client | null;
  onSuccess?: (invoice: any) => void;
}

interface RepOption {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  role: string;
}

// ── M2: extra talent on a booking ─────────────────────────────────
interface AdditionalTalentRow {
  talentId: string;
  role: string; // headliner | opener | special_guest | collab
  payoutCents: string; // string for input, parsed on submit
}

// ── M2: extra deposits on a run ───────────────────────────────────
interface DepositRow {
  label: string;
  amount: string; // dollars (decimal), converted to cents on submit
  dueDate: string; // YYYY-MM-DD
}

interface LogEventFormData {
  accountName: string;
  contactName: string;
  clientType: string;
  phoneNumber: string;
  emailAddress: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  timezone: string;
  talentId: string;
  eventOfferingId: string;
  eventDetails: string;
  rate: string;
  discount: string;
  dateOfEvent: string;
  eventTime: string;
  calendarNotes: string;
  notes: string;
  region: string;
  isTicketedEvent: boolean;
  isNoFeeShow: boolean;
  eventbriteEventId: string;
  rep: string;
  // Custom-booking duration (used when the built-in "Custom" option or an
  // existing no-set-rate offering is selected). Sets the exact calendar block.
  durationHours: string;
  durationMinutes: string;
  // End time shown when the built-in "Custom" type is selected.
  customEndTime: string;
  // ── M2: runs + multi-talent + multi-deposit ─────────────────────
  // Empty defaults — sections are hidden unless the workspace flags
  // `runsEnabled` / `multiTalentEnabled` are on.
  runId: string; // existing run id, or "" for none
  inlineRunName: string; // create new run with this name when set
  runSettlementMode: string; // per_show | per_run | custom
  additionalTalents: AdditionalTalentRow[];
  deposits: DepositRow[];
}

const TIMEZONE_OPTIONS = [
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "America/Phoenix", label: "Arizona (no DST)" },
  { value: "America/Anchorage", label: "Alaska Time (AKT)" },
  { value: "Pacific/Honolulu", label: "Hawaii Time (HST)" },
];

const getTimezoneForState = (state: string): string => {
  const stateTimezones: { [key: string]: string } = {
    "ME": "America/New_York", "NH": "America/New_York", "VT": "America/New_York",
    "MA": "America/New_York", "RI": "America/New_York", "CT": "America/New_York",
    "NY": "America/New_York", "NJ": "America/New_York", "PA": "America/New_York",
    "DE": "America/New_York", "MD": "America/New_York", "VA": "America/New_York",
    "WV": "America/New_York", "NC": "America/New_York", "SC": "America/New_York",
    "GA": "America/New_York", "FL": "America/New_York", "OH": "America/New_York",
    "MI": "America/New_York", "IN": "America/New_York", "KY": "America/New_York",
    "AL": "America/Chicago", "MS": "America/Chicago", "TN": "America/Chicago",
    "LA": "America/Chicago", "AR": "America/Chicago", "MO": "America/Chicago",
    "IL": "America/Chicago", "WI": "America/Chicago", "MN": "America/Chicago",
    "IA": "America/Chicago", "ND": "America/Chicago", "SD": "America/Chicago",
    "NE": "America/Chicago", "KS": "America/Chicago", "OK": "America/Chicago",
    "TX": "America/Chicago",
    "MT": "America/Denver", "WY": "America/Denver", "CO": "America/Denver",
    "NM": "America/Denver", "UT": "America/Denver", "ID": "America/Denver",
    "AZ": "America/Phoenix",
    "NV": "America/Los_Angeles", "CA": "America/Los_Angeles", "OR": "America/Los_Angeles",
    "WA": "America/Los_Angeles",
    "AK": "America/Anchorage",
    "HI": "Pacific/Honolulu",
  };
  const upperState = state.toUpperCase().trim();
  return stateTimezones[upperState] || "America/Chicago";
};

// Synthetic, always-available "Custom" entry for the Event Type dropdown.
// It is never a real stored offering — selecting it makes the Rate editable
// and reveals a Duration field whose value becomes the calendar block length.
const CUSTOM_OFFERING_ID = "__custom__";

const initialFormData: LogEventFormData = {
  accountName: "",
  contactName: "",
  clientType: "",
  isTicketedEvent: false,
  isNoFeeShow: false,
  eventbriteEventId: "",
  phoneNumber: "",
  emailAddress: "",
  address: "",
  city: "",
  state: "",
  zipCode: "",
  timezone: "America/Chicago",
  talentId: "",
  eventOfferingId: "",
  eventDetails: "",
  rate: "",
  discount: "0",
  dateOfEvent: "",
  eventTime: "",
  calendarNotes: "",
  notes: "",
  region: "",
  rep: "",
  durationHours: "1",
  durationMinutes: "0",
  customEndTime: "",
  // M2: runs + multi-talent + multi-deposit defaults
  runId: "",
  inlineRunName: "",
  runSettlementMode: "per_show",
  additionalTalents: [],
  deposits: [],
};

export function LogEventDialog({
  open,
  onOpenChange,
  preselectedClient,
  onSuccess,
}: LogEventDialogProps) {
  const { toast } = useToast();
  const queryClientHook = useQueryClient();
  const [formData, setFormData] = useState<LogEventFormData>(initialFormData);
  const [isRegionPopoverOpen, setIsRegionPopoverOpen] = useState(false);
  const [linkedClientId, setLinkedClientId] = useState<string | null>(null);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicateClients, setDuplicateClients] = useState<Client[]>([]);
  const [selectedDuplicateClientId, setSelectedDuplicateClientId] = useState<string | null>(null);

  const { data: profile } = useQuery<ProfileData>({
    queryKey: ["/api/profile"],
  });

  const { data: clients = [] } = useQuery<Client[]>({
    queryKey: ["/api/clients"],
  });

  const { data: regions = [] } = useQuery<{ id: string; name: string }[]>({
    queryKey: ["/api/regions"],
  });

  const { data: clientTypes = [] } = useQuery<ClientTypeRecord[]>({
    queryKey: ["/api/client-types"],
  });

  const { data: eventOfferings = [] } = useQuery<EventOffering[]>({
    queryKey: ["/api/event-offerings"],
  });

  const { data: talent = [] } = useQuery<Talent[]>({
    queryKey: ["/api/talent"],
  });

  const { data: repOptions = [] } = useQuery<RepOption[]>({
    queryKey: ["/api/rep-options"],
  });

  const { data: defaultClientType } = useQuery<ClientTypeRecord | null>({
    queryKey: ["/api/client-types/default"],
  });

  // ── M2: workspace toggles + active runs ──────────────────────────
  const { data: bookingFlags } = useQuery<{
    runsEnabled: boolean;
    multiTalentEnabled: boolean;
  }>({
    queryKey: ["/api/workspace/booking-flags"],
    enabled: open,
  });
  const runsEnabled = !!bookingFlags?.runsEnabled;
  const multiTalentEnabled = !!bookingFlags?.multiTalentEnabled;

  const { data: activeRuns = [] } = useQuery<Array<{
    id: string;
    name: string;
    status: string;
  }>>({
    queryKey: ["/api/runs"],
    enabled: open && runsEnabled,
  });
  
  // Fetch market pricing for current show offering to enable dynamic pricing
  const currentOffering = eventOfferings.find(o => o.id === formData.eventOfferingId);

  // Custom-booking mode: either the synthetic built-in "Custom" option, or an
  // existing no-set-rate offering. In both cases the Rate is editable and a
  // Duration field controls the calendar block length.
  const isBuiltInCustom = formData.eventOfferingId === CUSTOM_OFFERING_ID;
  const isCustomBooking = isBuiltInCustom || !!(currentOffering as any)?.noSetRate;
  const customDurationMinutes =
    Math.max(0, parseInt(formData.durationHours || "0", 10) || 0) * 60 +
    Math.max(0, parseInt(formData.durationMinutes || "0", 10) || 0);
  const { data: marketPricing = [] } = useQuery<Array<{ regionId: string; clientType: string | null; rate: string | null; isAvailable: boolean }>>({
    queryKey: ["/api/event-offerings", formData.eventOfferingId, "market-pricing"],
    enabled: !!formData.eventOfferingId && !!currentOffering?.hasDynamicPricing,
  });
  
  // Helper to get rate based on region, client type, and dynamic pricing
  const getRateForRegion = (regionName: string, clientType: string | null, offering: EventOffering | undefined) => {
    if (!offering) return "0";
    if (!offering.hasDynamicPricing) return offering.rate || "0";
    
    const region = regions.find(r => r.name === regionName);
    if (!region) return offering.rate || "0";
    
    // First try to find pricing that matches both region AND client type (most specific)
    let pricing = marketPricing.find(p => 
      p.regionId === region.id && p.clientType && p.clientType.toLowerCase() === clientType?.toLowerCase()
    );
    
    // Fall back to region-only pricing (clientType is null, applies to all types)
    if (!pricing) {
      pricing = marketPricing.find(p => p.regionId === region.id && !p.clientType);
    }
    
    // Fall back to any region match if no specific match found
    if (!pricing) {
      pricing = marketPricing.find(p => p.regionId === region.id);
    }
    
    if (!pricing) return offering.rate || "0";
    if (!pricing.isAvailable) return "0"; // Not available in this region
    
    return pricing.rate || offering.rate || "0";
  };
  
  // Update rate when market pricing data is fetched or region/clientType changes (for dynamic pricing)
  useEffect(() => {
    if (!open || formData.isTicketedEvent || formData.isNoFeeShow) return;
    // "No Set Rate" offerings are always entered manually — never auto-overwrite
    if ((currentOffering as any)?.noSetRate) return;
    if (!currentOffering?.hasDynamicPricing) return;
    if (marketPricing.length === 0) return;
    
    const newRate = getRateForRegion(formData.region, formData.clientType, currentOffering);
    if (newRate !== formData.rate) {
      setFormData(prev => ({ ...prev, rate: newRate }));
    }
  }, [marketPricing, formData.region, formData.clientType, currentOffering?.hasDynamicPricing, open]);

  const clientTypeOptions = useMemo(() => {
    return clientTypes
      .filter(ct => ct.isActive)
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [clientTypes]);

  const defaultTalent = talent.find(e => e.isDefault) || talent[0];

  // Filter offerings by selected talent
  const activeTalentId = formData.talentId || defaultTalent?.id;
  const filteredOfferings = activeTalentId
    ? eventOfferings.filter(o => o.talentId === activeTalentId)
    : eventOfferings;

  // Pre-populate form when preselectedClient changes
  useEffect(() => {
    if (open && preselectedClient) {
      const regionName = preselectedClient.regionId
        ? regions.find(r => r.id === preselectedClient.regionId)?.name || ""
        : "";

      const talentId = (preselectedClient as any).talentId || defaultTalent?.id || "";
      const talentOfferings = talentId
        ? eventOfferings.filter(o => o.talentId === talentId)
        : eventOfferings;
      const initOffering = talentOfferings.find(o => o.isDefault)
        || talentOfferings.find(o => o.name.toLowerCase().includes("full show"))
        || talentOfferings[0];

      setFormData({
        accountName: preselectedClient.accountName || "",
        contactName: preselectedClient.contactName || "",
        clientType: preselectedClient.clientType || defaultClientType?.name || "",
        phoneNumber: preselectedClient.phoneNumber || "",
        emailAddress: preselectedClient.emailAddress || "",
        address: preselectedClient.address || "",
        city: (preselectedClient as any).city || "",
        state: (preselectedClient as any).state || "",
        zipCode: (preselectedClient as any).zipCode || "",
        timezone: (preselectedClient as any).timezone || getTimezoneForState((preselectedClient as any).state || ""),
        talentId: talentId,
        eventOfferingId: (preselectedClient as any).eventOfferingId || initOffering?.id || "",
        eventDetails: preselectedClient.bookingDetails || initOffering?.name || "",
        rate: preselectedClient.rate?.toString() || initOffering?.rate || "",
        discount: "0",
        dateOfEvent: "",
        eventTime: "",
        calendarNotes: "",
        notes: "",
        region: regionName,
        isTicketedEvent: false,
        isNoFeeShow: false,
        rep: "",
        durationHours: "1",
        durationMinutes: "0",
      });
      setLinkedClientId(preselectedClient.id);
    } else if (open && !preselectedClient) {
      const talentId = defaultTalent?.id || "";
      const talentOfferings = talentId
        ? eventOfferings.filter(o => o.talentId === talentId)
        : eventOfferings;
      const initOffering = talentOfferings.find(o => o.isDefault)
        || talentOfferings.find(o => o.name.toLowerCase().includes("full show"))
        || talentOfferings[0];

      setFormData({
        ...initialFormData,
        clientType: defaultClientType?.name || "",
        talentId: talentId,
        eventOfferingId: initOffering?.id || "",
        eventDetails: initOffering?.name || "",
        rate: initOffering?.rate || "",
      });
      setLinkedClientId(null);
    }
  }, [open, preselectedClient, defaultClientType, regions, eventOfferings, defaultTalent]);

  // Duplicate detection function
  const findDuplicateClients = (data: LogEventFormData): Client[] => {
    if (linkedClientId) return [];

    const normalizeString = (s: string) => s.toLowerCase().trim();
    const normalizePhone = (p: string) => p.replace(/\D/g, "");

    return clients.filter((client) => {
      if (client.id === linkedClientId) return false;

      if (data.emailAddress && client.emailAddress) {
        if (normalizeString(data.emailAddress) === normalizeString(client.emailAddress)) {
          return true;
        }
      }

      if (data.address && client.address) {
        if (normalizeString(data.address) === normalizeString(client.address)) {
          return true;
        }
      }

      if (data.phoneNumber && client.phoneNumber) {
        if (normalizePhone(data.phoneNumber) === normalizePhone(client.phoneNumber)) {
          return true;
        }
      }

      if (data.accountName && client.accountName) {
        if (normalizeString(data.accountName) === normalizeString(client.accountName)) {
          return true;
        }
      }

      return false;
    });
  };

  const handleBookingSuccess = (result: BookingResult) => {
    console.log("[LogEventDialog] Booking orchestration success:", result);
    toast({
      title: "Show Logged",
      description: `Invoice #${result.data?.invoice.invoiceNumber} has been generated.`,
    });
    if (result.data?.calendar === null) {
      toast({
        title: "Calendar not pushed",
        description: "Talent hasn't connected their calendar yet. They've been sent a link to connect.",
      });
    }
    triggerConfetti();
    onOpenChange(false);
    setFormData(initialFormData);
    setLinkedClientId(null);
    setDuplicateClients([]);
    setSelectedDuplicateClientId(null);
    if (result.data?.invoice) {
      onSuccess?.(result.data.invoice);
    }
  };

  const [doubleBookConflict, setDoubleBookConflict] = useState<DoubleBookingConflict | null>(null);

  const handleDuplicatesFromOrchestrator = (candidates: DuplicateCandidate[]) => {
    console.log("[LogEventDialog] Duplicate candidates from orchestrator:", candidates);
    setDuplicateClients(candidates as unknown as Client[]);
    setSelectedDuplicateClientId(null);
    setShowDuplicateDialog(true);
  };

  const { orchestrate, orchestrateWithClientId, isPending: isOrchestrating } = useBookingOrchestration({
    onSuccess: handleBookingSuccess,
    onDuplicatesFound: handleDuplicatesFromOrchestrator,
    onDoubleBookingConflict: (conflict) => setDoubleBookConflict(conflict),
  });

  const runOrchestration = async (clientId?: string | null, forceCreate?: boolean) => {
    console.log("[LogEventDialog] Running orchestration with clientId:", clientId, "forceCreate:", forceCreate);
    
    await orchestrate({
      clientId: clientId || undefined,
      accountName: formData.accountName,
      contactName: formData.contactName,
      emailAddress: formData.emailAddress,
      phoneNumber: formData.phoneNumber,
      address: formData.address,
      city: formData.city,
      state: formData.state,
      zipCode: formData.zipCode,
      timezone: formData.timezone,
      clientType: formData.clientType,
      region: formData.region,
      talentId: formData.talentId,
      // Built-in "Custom" is not a stored offering — don't send a fake id.
      eventOfferingId: isBuiltInCustom ? undefined : formData.eventOfferingId,
      eventDetails: formData.eventDetails,
      // Custom/no-set-rate bookings drive the calendar block from this duration.
      customDurationMinutes: isCustomBooking ? customDurationMinutes : undefined,
      rate: formData.rate,
      discount: formData.discount,
      dateOfEvent: formData.dateOfEvent,
      eventTime: formData.eventTime,
      customEndTime: isBuiltInCustom ? formData.customEndTime : undefined,
      calendarNotes: formData.calendarNotes,
      notes: formData.notes,
      rep: formData.rep,
      isTicketedEvent: formData.isTicketedEvent,
      isNoFeeShow: formData.isNoFeeShow,
      eventbriteEnabled: formData.isTicketedEvent && !!formData.eventbriteEventId,
      eventbriteEventId: formData.isTicketedEvent && formData.eventbriteEventId
        ? formData.eventbriteEventId
        : null,
      forceCreateClient: forceCreate,
      // ── M2: runs + multi-talent + multi-deposit payload ─────────
      // The backend tolerates undefined/empty for backward compat.
      runId: formData.runId || undefined,
      inlineRun:
        !formData.runId && formData.inlineRunName.trim()
          ? { name: formData.inlineRunName.trim() }
          : undefined,
      runSettlementMode:
        formData.runId || formData.inlineRunName.trim()
          ? formData.runSettlementMode
          : undefined,
      bookingTalents: (() => {
        // First chip = primary talent (mirrors legacy `talentId`).
        // Additional rows are appended only when multi-talent is on.
        const primary = formData.talentId
          ? [{ talentId: formData.talentId, role: "headliner" }]
          : [];
        const extras = (formData.additionalTalents || [])
          .filter((t) => t.talentId)
          .map((t) => ({
            talentId: t.talentId,
            role: t.role || "collab",
            payoutType: t.payoutCents ? "flat" : "none",
            payoutAmountCents: t.payoutCents
              ? Math.round(parseFloat(t.payoutCents) * 100)
              : undefined,
          }));
        const rows = [...primary, ...extras];
        return rows.length > 0 ? rows : undefined;
      })(),
      runDeposits:
        formData.deposits && formData.deposits.length > 0
          ? formData.deposits
              .filter((d) => parseFloat(d.amount || "0") > 0)
              .map((d) => ({
                label: d.label || "Deposit",
                amountCents: Math.round(parseFloat(d.amount) * 100),
                dueDate: d.dueDate || undefined,
              }))
          : undefined,
    });
  };

  const handleSubmit = () => {
    // Validate required fields
    if (!formData.accountName.trim()) {
      toast({ title: "Error", description: "Account name is required.", variant: "destructive" });
      return;
    }
    if (!formData.rep.trim()) {
      toast({ title: "Error", description: "Rep is required. Please select a rep from the dropdown.", variant: "destructive" });
      return;
    }
    if (!formData.address.trim() || !formData.city.trim() || !formData.state.trim() || !formData.zipCode.trim()) {
      toast({ title: "Error", description: "Complete address is required.", variant: "destructive" });
      return;
    }
    if (!formData.dateOfEvent) {
      toast({ title: "Error", description: "Date of show is required.", variant: "destructive" });
      return;
    }
    if (!formData.eventTime.trim()) {
      toast({ title: "Error", description: "Show time is required.", variant: "destructive" });
      return;
    }
    if (isCustomBooking && customDurationMinutes <= 0) {
      toast({ title: "Error", description: "Duration must be greater than zero.", variant: "destructive" });
      return;
    }

    if (linkedClientId) {
      runOrchestration(linkedClientId);
      return;
    }

    // Let the orchestrator handle duplicate detection
    runOrchestration(null);
  };

  const handleDuplicateChoice = (choice: "link" | "create_new") => {
    setShowDuplicateDialog(false);
    if (choice === "link" && selectedDuplicateClientId) {
      setLinkedClientId(selectedDuplicateClientId);
      
      // Find the selected client and inherit their rate if form rate is empty
      const selectedClient = duplicateClients.find(c => c.id === selectedDuplicateClientId);
      const formRateValue = parseFloat(formData.rate) || 0;
      const clientRate = selectedClient?.rate?.toString() || "";
      
      // If form rate is 0 or empty and client has a rate, use client's rate
      if (formRateValue === 0 && clientRate && parseFloat(clientRate) > 0) {
        console.log("[LogEventDialog] Inheriting rate from linked client:", clientRate);
        setFormData(prev => ({ ...prev, rate: clientRate }));
        // Run orchestration with the updated rate
        orchestrate({
          clientId: selectedDuplicateClientId,
          accountName: formData.accountName,
          contactName: formData.contactName,
          emailAddress: formData.emailAddress,
          phoneNumber: formData.phoneNumber,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
          timezone: formData.timezone,
          clientType: formData.clientType,
          region: formData.region,
          talentId: formData.talentId,
          eventOfferingId: isBuiltInCustom ? undefined : formData.eventOfferingId,
          eventDetails: formData.eventDetails,
          customDurationMinutes: isCustomBooking ? customDurationMinutes : undefined,
          rate: clientRate, // Use inherited rate
          discount: formData.discount,
          dateOfEvent: formData.dateOfEvent,
          eventTime: formData.eventTime,
          customEndTime: isBuiltInCustom ? formData.customEndTime : undefined,
          calendarNotes: formData.calendarNotes,
          notes: formData.notes,
          rep: formData.rep,
          isTicketedEvent: formData.isTicketedEvent,
          isNoFeeShow: formData.isNoFeeShow,
          eventbriteEnabled: formData.isTicketedEvent && !!formData.eventbriteEventId,
          eventbriteEventId: formData.isTicketedEvent && formData.eventbriteEventId
            ? formData.eventbriteEventId
            : null,
          forceCreateClient: false,
        });
      } else {
        runOrchestration(selectedDuplicateClientId, false);
      }
    } else {
      // Create new client - pass null and force creation to skip duplicate check
      runOrchestration(null, true);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const regionName = formData.region || "";

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="w-screen h-[100dvh] max-w-full sm:w-full sm:h-auto sm:max-w-2xl sm:max-h-[90vh] rounded-none sm:rounded-lg overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <div className="flex items-center justify-between gap-4">
              <DialogTitle className="text-xl">Log an Event</DialogTitle>
              {regionName && <Badge variant="secondary">{regionName}</Badge>}
            </div>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {linkedClientId && (
              <div className="flex items-center gap-2 p-2 bg-primary/10 border border-primary/20 rounded-md text-sm">
                <Check className="h-4 w-4 text-primary" />
                <span>Linked to existing client - identity fields are locked to prevent data divergence</span>
              </div>
            )}

            {/* Contact Information */}
            <div className="space-y-4">
              <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                Contact Information
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="accountName">Account Name</Label>
                  <Input
                    id="accountName"
                    value={formData.accountName}
                    onChange={(e) => handleInputChange("accountName", e.target.value)}
                    placeholder="Business name"
                    disabled={!!linkedClientId}
                    className={linkedClientId ? "bg-muted" : ""}
                    data-testid="input-log-account-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contactName">Contact Name</Label>
                  <Input
                    id="contactName"
                    value={formData.contactName}
                    onChange={(e) => handleInputChange("contactName", e.target.value)}
                    placeholder="Primary contact"
                    disabled={!!linkedClientId}
                    className={linkedClientId ? "bg-muted" : ""}
                    data-testid="input-log-contact-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="clientType">Client Type</Label>
                  <Select
                    value={formData.clientType}
                    onValueChange={(value) => handleInputChange("clientType", value)}
                    disabled={!!linkedClientId}
                  >
                    <SelectTrigger id="clientType" className={linkedClientId ? "bg-muted" : ""} data-testid="select-log-client-type">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {clientTypeOptions.map((ct) => (
                        <SelectItem key={ct.id} value={ct.name}>{ct.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rep">Rep <span className="text-destructive">*</span></Label>
                  <Select
                    value={formData.rep}
                    onValueChange={(value) => handleInputChange("rep", value)}
                  >
                    <SelectTrigger id="rep" data-testid="select-log-rep">
                      <SelectValue placeholder="Select rep" />
                    </SelectTrigger>
                    <SelectContent>
                      {repOptions.map((rep) => (
                        <SelectItem key={rep.id} value={rep.fullName}>{rep.fullName}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phoneNumber">Phone Number</Label>
                  <Input
                    id="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={(e) => handleInputChange("phoneNumber", e.target.value)}
                    placeholder="(555) 123-4567"
                    disabled={!!linkedClientId}
                    className={linkedClientId ? "bg-muted" : ""}
                    data-testid="input-log-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="emailAddress">Email Address</Label>
                  <Input
                    id="emailAddress"
                    type="email"
                    value={formData.emailAddress}
                    onChange={(e) => handleInputChange("emailAddress", e.target.value)}
                    placeholder="email@example.com"
                    disabled={!!linkedClientId}
                    className={linkedClientId ? "bg-muted" : ""}
                    data-testid="input-log-email"
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Address */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-muted-foreground" />
                <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                  Address
                </h3>
              </div>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="address">Street Address <span className="text-destructive">*</span></Label>
                  {linkedClientId ? (
                    <Input
                      value={formData.address}
                      disabled
                      className="bg-muted"
                      data-testid="input-log-address-readonly"
                    />
                  ) : (
                    <AddressAutocomplete
                      value={formData.address}
                      onChange={(value) => handleInputChange("address", value)}
                      onAddressSelect={(components) => {
                        const newTimezone = getTimezoneForState(components.state);
                        setFormData(prev => ({
                          ...prev,
                          address: components.address,
                          city: components.city,
                          state: components.state,
                          zipCode: components.zipCode,
                          timezone: newTimezone,
                        }));
                      }}
                      placeholder="Start typing address..."
                      className="h-9 border rounded-md px-3"
                    />
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="city">City <span className="text-destructive">*</span></Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => handleInputChange("city", e.target.value)}
                      placeholder="City"
                      disabled={!!linkedClientId}
                      className={linkedClientId ? "bg-muted" : ""}
                      data-testid="input-log-city"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state">State <span className="text-destructive">*</span></Label>
                    <Input
                      id="state"
                      value={formData.state}
                      onChange={(e) => handleInputChange("state", e.target.value)}
                      placeholder="State"
                      disabled={!!linkedClientId}
                      className={linkedClientId ? "bg-muted" : ""}
                      data-testid="input-log-state"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="zipCode">Zip Code <span className="text-destructive">*</span></Label>
                    <Input
                      id="zipCode"
                      value={formData.zipCode}
                      onChange={(e) => handleInputChange("zipCode", e.target.value)}
                      placeholder="12345"
                      disabled={!!linkedClientId}
                      className={linkedClientId ? "bg-muted" : ""}
                      data-testid="input-log-zipcode"
                    />
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Booking */}
            <div className="space-y-4">
              <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                Booking
              </h3>

              {/* ── M2: Run sub-section (gated by workspace flag) ───── */}
              {runsEnabled && (
                <div className="space-y-3 rounded-md border border-dashed border-border p-3 bg-muted/30">
                  <div className="flex items-center justify-between gap-2">
                    <Label className="text-sm font-medium">Multi-show run</Label>
                    <span className="text-xs text-muted-foreground">
                      Group this booking with other shows
                    </span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">
                        Attach to existing run
                      </Label>
                      <Select
                        value={formData.runId || "_none"}
                        onValueChange={(v) =>
                          handleInputChange("runId", v === "_none" ? "" : v)
                        }
                      >
                        <SelectTrigger data-testid="select-log-run">
                          <SelectValue placeholder="None" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">None — single show</SelectItem>
                          {activeRuns.map((r) => (
                            <SelectItem key={r.id} value={r.id}>
                              {r.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">
                        …or create new run
                      </Label>
                      <Input
                        type="text"
                        placeholder="e.g. Spring Tour 2026"
                        value={formData.inlineRunName}
                        onChange={(e) =>
                          handleInputChange("inlineRunName", e.target.value)
                        }
                        disabled={!!formData.runId}
                        data-testid="input-log-inline-run-name"
                      />
                    </div>
                  </div>
                  {(formData.runId || formData.inlineRunName.trim()) && (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">
                        Invoice grouping
                      </Label>
                      <Select
                        value={formData.runSettlementMode}
                        onValueChange={(v) =>
                          handleInputChange("runSettlementMode", v)
                        }
                      >
                        <SelectTrigger data-testid="select-log-settlement-mode">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="per_show">
                            Per show — one invoice per booking
                          </SelectItem>
                          <SelectItem value="per_run">
                            Per run — single invoice covers all shows
                          </SelectItem>
                          <SelectItem value="custom">
                            Custom — group manually later
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Talent {multiTalentEnabled && "(primary)"}</Label>
                  <Select
                    value={formData.talentId || ""}
                    onValueChange={(value) => {
                      const talentOfferings = eventOfferings.filter(o => o.talentId === value);
                      const defaultOff = talentOfferings.find(o => o.isDefault) 
                        || talentOfferings.find(o => o.name.toLowerCase().includes("full show"))
                        || talentOfferings[0];
                      handleInputChange("talentId", value);
                      handleInputChange("eventOfferingId", defaultOff?.id || "");
                      handleInputChange("eventDetails", defaultOff?.name || "");
                      handleInputChange("rate", defaultOff?.rate || "0");
                    }}
                  >
                    <SelectTrigger data-testid="select-log-talent">
                      <SelectValue placeholder="Select talent" />
                    </SelectTrigger>
                    <SelectContent>
                      {talent.map((e) => (
                        <SelectItem key={e.id} value={e.id}>
                          {e.name} {e.isDefault && "(Default)"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* ── M2: Additional talent chips (gated by flag) ────── */}
                {multiTalentEnabled && (
                  <div className="space-y-2 rounded-md border border-dashed border-border p-3 bg-muted/30">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium">
                        Additional talent on this show
                      </Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setFormData((p) => ({
                            ...p,
                            additionalTalents: [
                              ...p.additionalTalents,
                              { talentId: "", role: "collab", payoutCents: "" },
                            ],
                          }))
                        }
                        data-testid="button-add-talent-chip"
                      >
                        + Add talent
                      </Button>
                    </div>
                    {formData.additionalTalents.length === 0 ? (
                      <p className="text-xs text-muted-foreground">
                        No co-billed talent. Click "Add talent" to add openers, special guests, or collaborators.
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {formData.additionalTalents.map((row, idx) => (
                          <div
                            key={idx}
                            className="grid grid-cols-12 gap-2 items-end"
                            data-testid={`row-additional-talent-${idx}`}
                          >
                            <div className="col-span-5">
                              <Select
                                value={row.talentId}
                                onValueChange={(v) =>
                                  setFormData((p) => ({
                                    ...p,
                                    additionalTalents: p.additionalTalents.map(
                                      (t, i) =>
                                        i === idx ? { ...t, talentId: v } : t,
                                    ),
                                  }))
                                }
                              >
                                <SelectTrigger
                                  data-testid={`select-additional-talent-${idx}`}
                                >
                                  <SelectValue placeholder="Talent" />
                                </SelectTrigger>
                                <SelectContent>
                                  {talent
                                    .filter(
                                      (t) =>
                                        t.id !== formData.talentId &&
                                        !formData.additionalTalents.some(
                                          (a, i) => i !== idx && a.talentId === t.id,
                                        ),
                                    )
                                    .map((t) => (
                                      <SelectItem key={t.id} value={t.id}>
                                        {t.name}
                                      </SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="col-span-3">
                              <Select
                                value={row.role}
                                onValueChange={(v) =>
                                  setFormData((p) => ({
                                    ...p,
                                    additionalTalents: p.additionalTalents.map(
                                      (t, i) => (i === idx ? { ...t, role: v } : t),
                                    ),
                                  }))
                                }
                              >
                                <SelectTrigger
                                  data-testid={`select-talent-role-${idx}`}
                                >
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="opener">Opener</SelectItem>
                                  <SelectItem value="special_guest">
                                    Special guest
                                  </SelectItem>
                                  <SelectItem value="collab">Collab</SelectItem>
                                  <SelectItem value="support">Support</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="col-span-3">
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="Payout $"
                                value={row.payoutCents}
                                onChange={(e) =>
                                  setFormData((p) => ({
                                    ...p,
                                    additionalTalents: p.additionalTalents.map(
                                      (t, i) =>
                                        i === idx
                                          ? { ...t, payoutCents: e.target.value }
                                          : t,
                                    ),
                                  }))
                                }
                                data-testid={`input-talent-payout-${idx}`}
                              />
                            </div>
                            <div className="col-span-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() =>
                                  setFormData((p) => ({
                                    ...p,
                                    additionalTalents: p.additionalTalents.filter(
                                      (_, i) => i !== idx,
                                    ),
                                  }))
                                }
                                data-testid={`button-remove-talent-${idx}`}
                              >
                                ×
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label>Event Type</Label>
                  <Select
                    value={formData.eventOfferingId || ""}
                    onValueChange={(value) => {
                      // Built-in "Custom" — always available, never a stored offering.
                      if (value === CUSTOM_OFFERING_ID) {
                        handleInputChange("eventOfferingId", CUSTOM_OFFERING_ID);
                        handleInputChange("eventDetails", "Custom");
                        handleInputChange("rate", ""); // editable, user types it
                        return;
                      }
                      // Switching away from Custom — clear the end time field.
                      handleInputChange("customEndTime", "");
                      const offering = filteredOfferings.find(o => o.id === value);
                      handleInputChange("eventOfferingId", value);
                      handleInputChange("eventDetails", offering?.name || "");
                      // "No Set Rate" offerings: clear the rate so the user MUST type one for this booking
                      if ((offering as any)?.noSetRate) {
                        handleInputChange("rate", "");
                      } else {
                        // Use dynamic pricing if available, otherwise use base rate
                        const rate = !formData.isTicketedEvent && !formData.isNoFeeShow
                          ? getRateForRegion(formData.region, formData.clientType, offering)
                          : "0";
                        handleInputChange("rate", rate);
                      }
                    }}
                  >
                    <SelectTrigger data-testid="select-log-event-details">
                      <SelectValue>{formData.eventDetails || "Select event type"}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {filteredOfferings.map((offering) => (
                        <SelectItem key={offering.id} value={offering.id}>
                          {offering.name} - ${parseFloat(offering.rate || "0").toFixed(2)}
                        </SelectItem>
                      ))}
                      <SelectItem value={CUSTOM_OFFERING_ID} data-testid="select-item-custom-offering">
                        Custom
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Duration — controls the calendar block length for custom bookings.
                    Shown for the built-in "Custom" option or any no-set-rate offering. */}
                {isCustomBooking && (
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Clock className="w-3 h-3" />
                      Duration
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="0"
                        max="23"
                        step="1"
                        value={formData.durationHours}
                        onChange={(e) => handleInputChange("durationHours", e.target.value)}
                        className="w-20"
                        data-testid="input-log-duration-hours"
                      />
                      <span className="text-sm text-muted-foreground">hr</span>
                      <Input
                        type="number"
                        min="0"
                        max="59"
                        step="5"
                        value={formData.durationMinutes}
                        onChange={(e) => handleInputChange("durationMinutes", e.target.value)}
                        className="w-20"
                        data-testid="input-log-duration-minutes"
                      />
                      <span className="text-sm text-muted-foreground">min</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Sets the exact length of the calendar block for this booking.
                    </p>
                  </div>
                )}
                
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-muted-foreground">
                      <DollarSign className="w-3 h-3" />
                      Rate
                    </Label>
                    {isCustomBooking ? (
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.rate}
                        onChange={(e) => handleInputChange("rate", e.target.value)}
                        placeholder="0.00"
                        data-testid="input-log-rate-custom"
                      />
                    ) : (
                      <div className="h-9 px-3 flex items-center text-sm font-medium bg-muted/50 rounded-md border" data-testid="text-log-rate">
                        ${parseFloat(formData.rate || "0").toFixed(2)}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="discount" className="flex items-center gap-2">
                      Discount
                    </Label>
                    <Input
                      id="discount"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.discount}
                      onChange={(e) => handleInputChange("discount", e.target.value)}
                      placeholder="0.00"
                      data-testid="input-log-discount"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 text-muted-foreground">
                      Total
                    </Label>
                    <div className="h-9 px-3 flex items-center text-sm font-semibold text-primary bg-primary/10 rounded-md border border-primary/20" data-testid="text-log-total">
                      ${Math.max(0, parseFloat(formData.rate || "0") - parseFloat(formData.discount || "0")).toFixed(2)}
                    </div>
                  </div>
                </div>
                
                {/* Ticketed Event / No Fee checkboxes */}
                <div className="flex flex-col gap-3 mt-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="isTicketedEvent"
                      checked={formData.isTicketedEvent}
                      onCheckedChange={(checked) => {
                        setFormData(prev => ({
                          ...prev,
                          isTicketedEvent: !!checked,
                          isNoFeeShow: checked ? false : prev.isNoFeeShow,
                          rate: checked ? "0" : prev.rate,
                        }));
                      }}
                      data-testid="checkbox-ticketed-event"
                    />
                    <Label htmlFor="isTicketedEvent" className="text-sm cursor-pointer">
                      Ticketed Event - settlement after event
                    </Label>
                  </div>
                  {formData.isTicketedEvent && (
                    <EventbriteEventPicker
                      value={formData.eventbriteEventId || ""}
                      onChange={(id) => setFormData(prev => ({ ...prev, eventbriteEventId: id }))}
                    />
                  )}
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="isNoFeeShow"
                      checked={formData.isNoFeeShow}
                      onCheckedChange={(checked) => {
                        setFormData(prev => ({
                          ...prev,
                          isNoFeeShow: !!checked,
                          isTicketedEvent: checked ? false : prev.isTicketedEvent,
                          rate: checked ? "0" : prev.rate,
                        }));
                      }}
                      data-testid="checkbox-no-fee-show"
                    />
                    <Label htmlFor="isNoFeeShow" className="text-sm cursor-pointer">
                      No fee - comp / promo event
                    </Label>
                  </div>
                </div>

                {/* ── M2: Deposits sub-section (only when run attached) ── */}
                {(formData.runId || formData.inlineRunName.trim()) && (
                  <div className="space-y-2 rounded-md border border-dashed border-border p-3 bg-muted/30 mt-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium">
                        Run-level deposits
                      </Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setFormData((p) => ({
                            ...p,
                            deposits: [
                              ...p.deposits,
                              { label: "Deposit", amount: "", dueDate: "" },
                            ],
                          }))
                        }
                        data-testid="button-add-deposit"
                      >
                        + Add deposit
                      </Button>
                    </div>
                    {formData.deposits.length === 0 ? (
                      <p className="text-xs text-muted-foreground">
                        No additional deposits. Add a hold deposit, milestone deposit, or balance-due deposit for the run.
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {formData.deposits.map((d, idx) => (
                          <div
                            key={idx}
                            className="grid grid-cols-12 gap-2 items-end"
                            data-testid={`row-deposit-${idx}`}
                          >
                            <div className="col-span-4">
                              <Input
                                type="text"
                                placeholder="Label (e.g. Hold deposit)"
                                value={d.label}
                                onChange={(e) =>
                                  setFormData((p) => ({
                                    ...p,
                                    deposits: p.deposits.map((x, i) =>
                                      i === idx ? { ...x, label: e.target.value } : x,
                                    ),
                                  }))
                                }
                                data-testid={`input-deposit-label-${idx}`}
                              />
                            </div>
                            <div className="col-span-3">
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="Amount $"
                                value={d.amount}
                                onChange={(e) =>
                                  setFormData((p) => ({
                                    ...p,
                                    deposits: p.deposits.map((x, i) =>
                                      i === idx ? { ...x, amount: e.target.value } : x,
                                    ),
                                  }))
                                }
                                data-testid={`input-deposit-amount-${idx}`}
                              />
                            </div>
                            <div className="col-span-4">
                              <Input
                                type="date"
                                value={d.dueDate}
                                onChange={(e) =>
                                  setFormData((p) => ({
                                    ...p,
                                    deposits: p.deposits.map((x, i) =>
                                      i === idx ? { ...x, dueDate: e.target.value } : x,
                                    ),
                                  }))
                                }
                                data-testid={`input-deposit-due-${idx}`}
                              />
                            </div>
                            <div className="col-span-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() =>
                                  setFormData((p) => ({
                                    ...p,
                                    deposits: p.deposits.filter(
                                      (_, i) => i !== idx,
                                    ),
                                  }))
                                }
                                data-testid={`button-remove-deposit-${idx}`}
                              >
                                ×
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <Separator />

            {/* Schedule */}
            <div className="space-y-4">
              <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                Schedule
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="dateOfEvent" className="flex items-center gap-2">
                    <Calendar className="w-3 h-3" />
                    Date of Event <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="dateOfEvent"
                    type="date"
                    value={formData.dateOfEvent}
                    onChange={(e) => handleInputChange("dateOfEvent", e.target.value)}
                    data-testid="input-log-date-of-show"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="eventTime" className="flex items-center gap-2">
                    <Clock className="w-3 h-3" />
                    {isBuiltInCustom ? "Load In" : "Event Time"} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="eventTime"
                    type="text"
                    value={formData.eventTime}
                    onChange={(e) => handleInputChange("eventTime", e.target.value)}
                    placeholder="e.g. 2:00p"
                    data-testid="input-log-event-time"
                  />
                </div>
                {isBuiltInCustom && (
                  <div className="space-y-2">
                    <Label htmlFor="customEndTime" className="flex items-center gap-2">
                      <Clock className="w-3 h-3" />
                      End Time
                    </Label>
                    <Input
                      id="customEndTime"
                      type="text"
                      value={formData.customEndTime}
                      onChange={(e) => handleInputChange("customEndTime", e.target.value)}
                      placeholder="e.g. 9:00p"
                      data-testid="input-log-custom-end-time"
                    />
                  </div>
                )}
                <div className="space-y-2 col-span-2">
                  <Label htmlFor="timezone" className="flex items-center gap-2">
                    <Globe className="w-3 h-3" />
                    Timezone
                  </Label>
                  <Select
                    value={formData.timezone}
                    onValueChange={(value) => handleInputChange("timezone", value)}
                  >
                    <SelectTrigger data-testid="select-log-timezone">
                      <SelectValue>{TIMEZONE_OPTIONS.find(tz => tz.value === formData.timezone)?.label || formData.timezone}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {TIMEZONE_OPTIONS.map((tz) => (
                        <SelectItem key={tz.value} value={tz.value}>
                          {tz.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <Separator />

            {/* Notes Section */}
            <div className="space-y-4">
              <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                Notes
              </h3>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="calendarNotes">Calendar Notes</Label>
                  <Textarea
                    id="calendarNotes"
                    value={formData.calendarNotes}
                    onChange={(e) => handleInputChange("calendarNotes", e.target.value)}
                    placeholder="Notes to include in the calendar event..."
                    rows={2}
                    data-testid="textarea-log-calendar-notes"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes">Internal Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => handleInputChange("notes", e.target.value)}
                    placeholder="Internal notes about this client..."
                    rows={2}
                    data-testid="textarea-log-notes"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between gap-4 pt-4 border-t border-border">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="button-log-cancel">
              Close
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isOrchestrating}
              data-testid="button-log-event-submit"
            >
              {isOrchestrating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Plus className="w-4 h-4 mr-2" />
              Log Event
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Duplicate Detection Dialog */}
      <AlertDialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              Potential Duplicate Found
            </AlertDialogTitle>
            <AlertDialogDescription>
              We found {duplicateClients.length} existing client{duplicateClients.length > 1 ? 's' : ''} that match the information you entered. Would you like to link this event to an existing client or create a new one?
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="my-4 space-y-2 max-h-48 overflow-y-auto">
            <RadioGroup value={selectedDuplicateClientId || ""} onValueChange={setSelectedDuplicateClientId}>
              {duplicateClients.map((client) => (
                <div 
                  key={client.id} 
                  className={cn(
                    "flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors",
                    selectedDuplicateClientId === client.id ? "border-primary bg-primary/5" : "hover:bg-muted/50"
                  )}
                  onClick={() => setSelectedDuplicateClientId(client.id)}
                >
                  <RadioGroupItem value={client.id} id={client.id} className="mt-1" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{client.accountName || "Unnamed"}</p>
                    {client.emailAddress && (
                      <p className="text-xs text-muted-foreground truncate">{client.emailAddress}</p>
                    )}
                    {client.address && (
                      <p className="text-xs text-muted-foreground truncate">{client.address}</p>
                    )}
                  </div>
                </div>
              ))}
            </RadioGroup>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowDuplicateDialog(false)}>
              Cancel
            </AlertDialogCancel>
            <Button
              variant="outline"
              onClick={() => handleDuplicateChoice("create_new")}
            >
              Create New Client
            </Button>
            <AlertDialogAction
              disabled={!selectedDuplicateClientId}
              onClick={() => handleDuplicateChoice("link")}
            >
              Link to Selected
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {doubleBookConflict && (
        <AlertDialog open={true} onOpenChange={(o) => { if (!o) setDoubleBookConflict(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Double Booking Detected</AlertDialogTitle>
              <AlertDialogDescription>{doubleBookConflict.message}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setDoubleBookConflict(null)}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                data-testid="button-force-book-confirm"
                onClick={() => {
                  const pending = doubleBookConflict.pendingFormData;
                  setDoubleBookConflict(null);
                  orchestrate({ ...pending, forceTalentOverlap: true });
                }}
              >
                Book Anyway
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
}
